/*
 * useSessionStorageEncoded.ts
 * useSessionStorageEncoded hook
 *
 * Created by Nick Perry on 11/07/2022.
 *
 *
 *
 * Copyright © 2009-2023 United States Government as represented by
 * the Program Manager of the DHA: Web and Mobile Technology Program Management Office.
 * All Rights Reserved.
 *
 * Copyright © 2009-2023 Contributors. All Rights Reserved.
 *
 * THIS OPEN SOURCE AGREEMENT ("AGREEMENT") DEFINES THE RIGHTS OF USE,
 * REPRODUCTION, DISTRIBUTION, MODIFICATION AND REDISTRIBUTION OF CERTAIN
 * COMPUTER SOFTWARE ORIGINALLY RELEASED BY THE UNITED STATES GOVERNMENT
 * AS REPRESENTED BY THE GOVERNMENT AGENCY LISTED BELOW ("GOVERNMENT AGENCY").
 * THE UNITED STATES GOVERNMENT, AS REPRESENTED BY GOVERNMENT AGENCY, IS AN
 * INTENDED THIRD-PARTY BENEFICIARY OF ALL SUBSEQUENT DISTRIBUTIONS OR
 * REDISTRIBUTIONS OF THE SUBJECT SOFTWARE. ANYONE WHO USES, REPRODUCES,
 * DISTRIBUTES, MODIFIES OR REDISTRIBUTES THE SUBJECT SOFTWARE, AS DEFINED
 * HEREIN, OR ANY PART THEREOF, IS, BY THAT ACTION, ACCEPTING IN FULL THE
 * RESPONSIBILITIES AND OBLIGATIONS CONTAINED IN THIS AGREEMENT.
 *
 * Government Agency: DHA: Web and Mobile Technology Program Management Office
 * Government Agency Original Software Designation: dha-login
 * Government Agency Original Software Title: dha-login
 * User Registration Requested. Please send email
 * with your contact information to: robert.a.kayl.civ@mail.mil
 * Government Agency Point of Contact for Original Software - Program Manager: robert.a.kayl.civ@mail.mil
 */
import { useState, useEffect, useContext } from 'react';

import { decrypt } from 'dha-encryption';

import { LoginDatabaseContext } from '../providers/LoginDatabaseProvider';

function useSessionStorageEncoded(
  key: string,
  ttl: number
): [boolean, (newValue: boolean) => void] {
  // loading state should start as 'true' to allow async authentication functionality to "settle" before loading the app.
  const [encodeString, setEncodeString] = useState<string>('');
  const { DHA_Key, database } = useContext(LoginDatabaseContext);

  const [authenticated, setAuthenticated] = useState<boolean>(false);

  // useEffect to get indexDB values needed for encoding
  useEffect(() => {
    const asyncFunc = async () => {
      if (DHA_Key === undefined) {
        return;
      }
      try {
        const encryptedUser = await database.userProfile.get('primaryUser');

        if (encryptedUser !== undefined) {
          const decryptedUser = JSON.parse(await decrypt(encryptedUser.userProfile, DHA_Key));

          // store sec question answers separated with a space as encodeString
          setEncodeString(`${decryptedUser.answer1} ${decryptedUser.answer2}`);
        }
      } catch (err) {
        console.log(err);
      }
    };
    asyncFunc().catch((err) => console.log(err));
  }, [DHA_Key, database.userProfile]);

  // 2nd useEffect to set authenticated state based on values retrieved from indexDB table
  useEffect(() => {
    // Get from local storage by key
    const item = getWithExpiry(key);

    if (item !== null && encodeString) {
      if (window.atob(item) === encodeString) {
        setAuthenticated(true);
      }
    }
  }, [encodeString, key]);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to SessionStorage.
  const setValue = (value: boolean | ((val: boolean) => boolean)) => {
    setAuthenticated(value);
    if (value) {
      // Save to local storage with an expiration of 1 hour
      setWithExpiry(key, window.btoa(encodeString), ttl);
    } else {
      window.sessionStorage.removeItem(key);
    }
  };

  return [authenticated, setValue];
}

function setWithExpiry(key: string, value: string, ttl: number) {
  const now = new Date();

  // `item` is an object which contains the original value
  // as well as the time when it's supposed to expire
  const item = {
    value: value,
    expiry: now.getTime() + ttl,
  };
  window.sessionStorage.setItem(key, JSON.stringify(item));
}

function getWithExpiry(key: string): string | null {
  const itemStr = window.sessionStorage.getItem(key);
  // if the item doesn't exist, return null
  if (!itemStr) {
    return null;
  }
  const item = JSON.parse(itemStr) as { value: string; expiry: number };
  const now = new Date();
  // compare the expiry time of the item with the current time
  if (now.getTime() > item.expiry) {
    // If the item is expired, delete the item from storage
    // and return null
    window.sessionStorage.removeItem(key);
    return null;
  }
  return item.value;
}

export default useSessionStorageEncoded;
